// A run's Costs & invoices: one list of everything the run cost, by layer. Rows from records kept elsewhere (PO lines,
// landed-cost charges on the run's POs, the make PO's fees, tooling the run pays for) are read-only and say where they
// come from, so nothing is entered twice; the run's own entries (freight and broker invoices, packing, labour,
// certification, payment fees) are added here with their invoice number, real date, paid status and files. Lines on
// the run's POs that aren't its builds' are apart: run goods (paid by the run, not in the cost per unit) and lines
// left out (another project's parts on the same PO). What still needs someone is listed first.

import { AlertTriangle, Lock, Plus, Search } from "lucide-react";
import { useState } from "react";
import { numberOf } from "../../../../../shared/calc.ts";
import { type Attention, type RowBill, type RunCostRow, type RunResult, runResult } from "../../../../../shared/costs.ts";
import type { ProductionCost } from "../../../../../shared/db.gen.ts";
import { PRESALE_FILE_HINT } from "../../../../../shared/presales.ts";
import {
  CATEGORY_LAYER,
  COST_BASIS_LABELS,
  COST_CATEGORIES,
  COST_CATEGORY_LABELS,
  type CostCategory,
  LAYER_HINTS,
  LAYER_LABELS,
  LAYERS,
  ONE_TIME_CATEGORIES,
  PAID_METHOD_LABELS,
  PAID_METHODS,
} from "../../../../../shared/runs.ts";
import { useCanPay } from "../../../lib/bills.ts";
import { centsText, shortDate, useCostIndex } from "../../../lib/costs.ts";
import { money } from "../../../lib/format.ts";
import { can, mutate } from "../../../lib/mutate.ts";
import { useRows } from "../../../lib/sync.ts";
import { Attachments } from "../../../ui/Attachments.tsx";
import { Badge, Button, cx, Input, Select, Textarea, UpperInput } from "../../../ui/controls.tsx";
import { type Column, DataTable, type TableSection } from "../../../ui/DataTable.tsx";
import { DialogLayer } from "../../../ui/DialogLayer.tsx";
import { Figure, Figures } from "../../../ui/PageParts.tsx";
import { PaneLink } from "../../../ui/SidePane.tsx";
import { toast } from "../../../ui/toast.tsx";
import { Why } from "../../../ui/Why.tsx";
import { PaidWith } from "../../bills/PaidWith.tsx";
import { WisePicker } from "../../bills/WisePicker.tsx";
import { Card } from "../Workspace.tsx";
import type { RunProps } from "./RunsTab.tsx";

const KIND_LABEL: Record<RunCostRow["kind"], string> = {
  po_line: "PO line",
  make_fee: "Make PO",
  landed: "Landed cost",
  manual: "Entered here",
  tooling: "Tooling ledger",
};

type CostRow = RunCostRow & { id: string };

/** The layer a cost counts in, then run goods and lines left out: the table's bands, each with what it adds up to. */
function bandOf(res: RunResult) {
  const total = (rows: RunCostRow[]) => rows.reduce((n, r) => n + r.cents, 0);
  const label = (title: string, cents: number, hint: string, muted?: boolean) => (
    <>
      {title}
      <span className={cx("font-mono normal-case", muted ? "text-faint" : "text-text")}>{centsText(cents)}</span>
      <span className="font-normal text-faint normal-case">{hint}</span>
    </>
  );
  const layers = new Map(
    LAYERS.map((l, i) => {
      const cents = total(res.rows.filter((r) => r.counts === "unit" && r.layer === l));
      return [l, { key: l, order: i, label: label(LAYER_LABELS[l], cents, LAYER_HINTS[l]) }];
    }),
  );
  const goods: TableSection = {
    key: "run_goods",
    order: LAYERS.length,
    label: label(
      "Run goods",
      res.totals.runGoodsCents,
      "Paid for by the run but not part of its builds (cables for backers): in the run's spend, never in the cost per unit",
    ),
  };
  const leftOut: TableSection = {
    key: "left_out",
    order: LAYERS.length + 1,
    label: label(
      "Left out: not this run's",
      res.totals.leftOutCents,
      "Lines on the run's POs for things its builds don't use (another project's parts on the same PO): not counted here at all",
      true,
    ),
  };
  return (r: RunCostRow): TableSection | null =>
    r.counts === "unit" ? (layers.get(r.layer) ?? null) : r.counts === "run_goods" ? goods : r.counts === "left_out" ? leftOut : null;
}

/** Bills and payments --- what a row's bills say, for Copy / CSV and sorting. */
const billsText = (r: RunCostRow) =>
  r.bills
    .map((b) => `${b.status === "paid" ? "Paid" : b.status === "scheduled" ? "Being paid" : b.status === "draft" ? "Draft bill" : "Billed"} ${b.number}`)
    .join(", ");

export function RunCosts({ run, project }: RunProps) {
  const cx_ = useCostIndex();
  const res = runResult(cx_, run.id);
  const [editing, setEditing] = useState<string | "new" | null>(null);
  const [slot, setSlot] = useState<HTMLDivElement | null>(null); // Table views --- by the card's title
  const attachments = useRows("attachment");
  // Costs that came by email: "Email · Nov 2" (its doc date) where an invoice number would be
  const emailOf = new Map(
    attachments
      .filter((f) => f.owner_type === "production_cost" && f.kind === "email")
      .map((f) => [f.owner_id, f.doc_date ? `Email · ${shortDate(String(f.doc_date).slice(0, 10))}` : "Email"] as const),
  );
  if (!res) return null;
  // A past run is closed from the start, and takes its costs and invoices from paper any time (as it does its POs).
  const editable = can("production_cost.update") && (run.status !== "closed" || run.historical);
  // Every row the run's costs come from, in its layer's band (then run goods and lines left out); pre-sale shipping to
  // backers is the pre-sale's, shown on its tab.
  const rows: CostRow[] = res.rows.filter((r) => r.counts !== "presale").map((r) => ({ ...r, id: r.key }));
  const columns: Column<CostRow>[] = [
    {
      key: "date",
      header: "Dated",
      width: "88px",
      value: (r) => r.date ?? "",
      cell: (r) => <span className="text-[12px] text-muted">{shortDate(r.date) || "—"}</span>,
    },
    {
      key: "kind",
      header: "From",
      width: "120px",
      value: (r) => KIND_LABEL[r.kind],
      cell: (r) => (
        <span className="flex min-w-0 items-center gap-1 text-[12px]">
          {r.kind !== "manual" && <Lock className="size-3 shrink-0 text-faint" aria-label="Read-only" />}
          <span className="truncate" title={r.kind !== "manual" ? `Recorded on its ${KIND_LABEL[r.kind].toLowerCase()}: change it there` : undefined}>
            {KIND_LABEL[r.kind]}
          </span>
        </span>
      ),
    },
    { key: "category", header: "Category", width: "120px", cell: (r) => <span className="truncate text-muted">{r.category}</span> },
    { key: "vendor", header: "Vendor", width: "minmax(100px,1fr)", cell: (r) => <span className="truncate text-muted">{r.vendor}</span> },
    {
      key: "description",
      header: "What",
      width: "minmax(180px,2fr)",
      value: (r) => [r.description, ...r.flags].filter(Boolean).join(" · "),
      text: (r) => r.description,
      cell: (r) => (
        <>
          <span className="min-w-0 truncate" title={r.description || undefined}>
            {r.description || "—"}
          </span>
          {r.flags.map((f) => (
            <span key={f} className="shrink-0 text-[11px] text-warn">
              {f}
            </span>
          ))}
        </>
      ),
    },
    {
      key: "bills",
      header: "Billed · paid",
      width: "minmax(110px,1fr)",
      value: (r) => [billsText(r), r.paid].filter(Boolean).join(" · "),
      cell: (r) => (
        <>
          <BillChips bills={r.bills} />
          {r.paid && (
            <span title={r.paid}>
              <Badge tone="ok">{r.paid.split(" · ")[0]}</Badge>
            </span>
          )}
        </>
      ),
    },
    {
      key: "ref",
      header: "Ref",
      width: "110px",
      // A cost that came by email has no invoice number: its email (kind Email, migration 076) is its reference
      value: (r) => r.ref || (r.costId && emailOf.get(r.costId)) || "",
      cell: (r) =>
        !r.ref && r.costId && emailOf.has(r.costId) ? (
          <span className="truncate text-[12px] text-muted">{emailOf.get(r.costId)}</span>
        ) : r.poId && r.kind !== "manual" ? (
          <PaneLink kind="po" id={r.poId} to={`/pos?open=${r.poId}`} className="truncate font-mono text-[12px] hover:underline">
            {r.ref}
          </PaneLink>
        ) : (
          <span className="truncate font-mono text-[12px]">{r.ref || <span className="text-faint">—</span>}</span>
        ),
    },
    {
      key: "amount",
      header: "Amount",
      headerTitle: "In its own currency (USD costs show only in USD)",
      width: "120px",
      align: "right",
      value: (r) => (r.currency === "USD" ? null : r.amount),
      text: (r) => (r.currency === "USD" ? "" : money(r.amount, r.currency)),
      cell: (r) => <span className="font-mono text-[12px]">{r.currency === "USD" ? "" : money(r.amount, r.currency)}</span>,
    },
    {
      key: "usd",
      header: "USD",
      width: "110px",
      align: "right",
      value: (r) => r.cents / 100,
      cell: (r) => (
        <span className="font-mono text-[12px]">
          <Why
            title={r.description || r.category}
            lines={[
              ...(r.currency !== "USD" ? [{ label: `${money(r.amount, r.currency)} at ${r.usdRate} USD per ${r.currency}`, value: centsText(r.cents) }] : []),
              { label: "How it's spread", detail: r.spread },
              ...(r.paid ? [{ label: r.paid }] : []),
            ]}
            note={
              r.kind === "manual" ? "Entered on this run: click the row to change it" : `Read from its ${KIND_LABEL[r.kind].toLowerCase()}; change it there`
            }
          >
            {centsText(r.cents)}
          </Why>
        </span>
      ),
    },
    {
      key: "files",
      header: "Files",
      // Files fits its column (the chips shrink), so a cost with two invoices never runs past the table.
      width: "minmax(140px,220px)",
      value: () => "",
      cell: (r) => (
        <>
          {r.owner && <Attachments owner={r.owner} compact disabled={!can("attachment.update")} />}
          <RunGoodsToggle r={r} />
        </>
      ),
    },
  ];
  return (
    <>
      {res.attention.length > 0 && <NeedsAttention items={res.attention} onOpen={editable ? setEditing : undefined} />}
      {/* What it spent, in one number (Mitch, 2026-11-04: fewer numbers; what a unit cost is the Landed cost tab) */}
      <div className="flex items-start gap-3">
        <Figures className="flex-1">
          <Figure
            label="Spent on the run"
            value={
              <Why title="Spent on the run" note={res.reconcile[0]?.why}>
                {centsText(res.totals.spentCents)}
              </Why>
            }
          />
        </Figures>
        {editable && (
          <Button variant="primary" icon={<Plus className="size-3.5" />} onClick={() => setEditing("new")}>
            Add cost
          </Button>
        )}
      </div>
      {rows.length > 0 && (
        <Card
          boxed={false}
          title="Costs & invoices"
          note="By layer, then run goods and lines left out"
          actions={<div ref={setSlot} className="flex items-center gap-1" />}
        >
          <DataTable
            fit={20}
            rows={rows}
            columns={columns}
            sectionOf={bandOf(res)}
            defaultSort={{ key: "date" }}
            onOpen={editable ? (r) => r.costId && setEditing(r.costId) : undefined}
            exportName="Run costs"
            viewsSlot={slot}
            pageFilters={false}
            rowNoun={["cost", "costs"]}
          />
        </Card>
      )}
      {!res.rows.length && (
        <div className="rounded-lg border border-dashed border-line-strong bg-surface p-6 text-center text-muted">
          Nothing yet. PO lines, landed costs and the make PO's fees show here as soon as their POs are on the run; add freight, broker, packing and other
          invoices with Add cost.
        </div>
      )}
      <div className="text-[11px] text-faint">
        <Lock className="mr-1 inline size-3" />
        Read-only rows are kept on their PO, landed cost or the tooling ledger ({project.code || "project"} → Costs), so they're never entered twice. Attach
        their invoices right here: they're filed on the PO or tooling item.
      </div>
      {editing && <CostDialog run={run} id={editing === "new" ? null : editing} onClose={() => setEditing(null)} />}
    </>
  );
}

/** Bills and payments --- the bills a row is on: "Billed BL-1004" until it's paid, then "Paid BL-1004"; each opens the bill. */
function BillChips({ bills }: { bills: RowBill[] }) {
  if (!bills.length) return null;
  return (
    <>
      {bills.map((b) => (
        <PaneLink
          kind="bill"
          id={b.id}
          key={b.id}
          to={`/bills?tab=bills&status=all&bill=${b.id}`}
          className="mr-1.5 inline-flex align-middle"
          title={`${b.number}${b.invoice ? ` (${b.invoice})` : ""}: ${b.status === "paid" ? "paid" : b.status === "scheduled" ? "a payment is on its way" : b.status === "draft" ? "a draft bill" : "billed, not paid yet"}`}
        >
          <Badge tone={b.status === "paid" ? "ok" : b.status === "scheduled" ? "warn" : b.status === "draft" ? "neutral" : "accent"}>
            {b.status === "paid" ? "Paid" : b.status === "scheduled" ? "Being paid" : b.status === "draft" ? "Draft bill" : "Billed"} {b.number}
          </Badge>
        </PaneLink>
      ))}
    </>
  );
}

/** A PO line apart from the builds: count it as run goods (the run paid for it), or take that back. */
function RunGoodsToggle({ r }: { r: RunCostRow }) {
  if (r.kind !== "po_line" || !r.poLineId || r.counts === "unit" || !can("po_line.set_run_goods")) return null;
  const goods = r.counts === "run_goods";
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        mutate("po_line.set_run_goods", { ids: [r.poLineId as string], run_goods: !goods });
      }}
      className="ml-1 text-[11px] text-accent hover:underline"
      title={
        goods
          ? "Not the run's after all: leave it out of the run's spend"
          : "The run paid for it (cables for backers): count it in the run's spend, not in the cost per unit"
      }
    >
      {goods ? "Not run goods" : "Run goods"}
    </button>
  );
}

/** Costs that still need someone: a category not known yet, no invoice, a foreign amount at a rate of 1. */
function NeedsAttention({ items, onOpen }: { items: Attention[]; onOpen?: (id: string) => void }) {
  return (
    <section className="rounded-lg border border-warn/40 bg-warn-soft/40 px-4 py-2.5">
      <div className="flex items-center gap-1.5 text-[13px] font-semibold">
        <AlertTriangle className="size-3.5 text-warn" /> Needs attention
        <span className="font-normal text-muted">
          {items.length} {items.length === 1 ? "cost" : "costs"}
        </span>
      </div>
      <ul className="mt-1 flex flex-col gap-0.5 text-[12px]">
        {items.map((a) => (
          <li key={a.key} className="flex items-center gap-2">
            {a.costId && onOpen ? (
              <button type="button" onClick={() => onOpen(a.costId as string)} className="min-w-0 truncate text-left hover:underline">
                {a.what}
              </button>
            ) : (
              <span className="min-w-0 truncate">{a.what}</span>
            )}
            <span className="shrink-0 text-warn">needs {a.needs.join(", ")}</span>
            {a.fee && can("payment.fee_decide") && <FeeDecision paymentId={a.fee.paymentId} review={a.fee.review} />}
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Bills and payments --- a payment's fee that only matched a hand-entered one by amount: the same fee, or add it. */
function FeeDecision({ paymentId, review }: { paymentId: string; review: string }) {
  const [busy, setBusy] = useState(false);
  const decide = async (add: boolean) => {
    setBusy(true);
    await mutate("payment.fee_decide", { id: paymentId, add });
    setBusy(false);
  };
  return (
    <span className="ml-auto flex shrink-0 items-center gap-1" title={review}>
      <Button size="sm" variant="ghost" disabled={busy} onClick={() => decide(false)}>
        Same fee
      </Button>
      <Button size="sm" variant="ghost" disabled={busy} onClick={() => decide(true)}>
        Add it
      </Button>
    </span>
  );
}

type Draft = Omit<
  ProductionCost,
  "id" | "run_id" | "created_at" | "updated_at" | "created_by" | "external_ref" | "presale_id" | "presale_shipment_id" | "shipping_kind"
> & { amountText: string; rateText: string };

function CostDialog({ run, id, onClose }: { run: RunProps["run"]; id: string | null; onClose: () => void }) {
  const costs = useRows("production_cost");
  const vendors = useRows("vendor");
  const cx_ = useCostIndex();
  const existing = id ? costs.find((c) => c.id === id) : undefined;
  const [costId] = useState(() => id ?? crypto.randomUUID());
  const [saved, setSaved] = useState(!!existing);
  const [d, setD] = useState<Draft>(() => ({
    category: existing?.category ?? "freight",
    vendor_id: existing?.vendor_id ?? null,
    description: existing?.description ?? "",
    amount: Number(existing?.amount ?? 0),
    amountText: existing ? String(existing.amount) : "",
    currency: existing?.currency ?? "USD",
    usd_rate: Number(existing?.usd_rate ?? 1),
    rateText: existing && existing.currency !== "USD" ? String(existing.usd_rate) : "",
    basis: existing?.basis ?? "one_time",
    invoice_number: existing?.invoice_number ?? "",
    invoice_date: existing?.invoice_date ? String(existing.invoice_date).slice(0, 10) : null,
    po_id: existing?.po_id ?? null,
    paid_on: existing?.paid_on ? String(existing.paid_on).slice(0, 10) : null,
    paid_method: existing?.paid_method ?? "",
    paid_ref: existing?.paid_ref ?? "",
    paid_account_id: existing?.paid_account_id ?? null, // Which card ---
    paid_via: existing?.paid_via ?? "",
    notes: existing?.notes ?? "",
  }));
  const set = (patch: Partial<Draft>) => setD({ ...d, ...patch });
  const pos = cx_.posByRun.get(run.id) ?? [];
  const usd = d.currency.toUpperCase() === "USD";
  const amount = numberOf(d.amountText);
  const rate = usd ? 1 : numberOf(d.rateText);
  const oneTime = ONE_TIME_CATEGORIES.has(d.category as CostCategory);
  const save = async (confirmDuplicate = false) => {
    if (!Number.isFinite(amount) || d.amountText.trim() === "") return toast.error("Enter the amount");
    if (!usd && !(rate > 0)) return toast.error(`Enter the USD rate for ${d.currency} (from the invoice, or the day's rate)`);
    const fields = {
      category: d.category,
      vendor_id: d.vendor_id,
      description: d.description,
      amount,
      currency: d.currency.toUpperCase(),
      usd_rate: rate,
      basis: d.basis,
      invoice_number: d.invoice_number,
      invoice_date: d.invoice_date || null,
      po_id: d.po_id,
      paid_on: d.paid_on || null,
      paid_method: d.paid_on ? d.paid_method : ("" as const),
      paid_ref: d.paid_ref,
      paid_account_id: d.paid_on ? d.paid_account_id : null,
      paid_via: (d.paid_on ? d.paid_via : "") as never,
      notes: d.notes,
    };
    if (saved) {
      if (await mutate("production_cost.update", { id: costId, patch: fields })) onClose();
      return;
    }
    const res = await fetch("/api/mutate", {
      method: "POST",
      headers: { "content-type": "application/json", "x-requested-with": "hhl-ops" },
      body: JSON.stringify({ name: "production_cost.create", args: { id: costId, run_id: run.id, ...fields, confirm_duplicate: confirmDuplicate } }),
    });
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    if (res.status === 409 && !confirmDuplicate) {
      if (confirm(`${body.error}\n\nAdd it anyway, as a separate cost?`)) return save(true);
      return;
    }
    if (!res.ok) return toast.error(body.error ?? "Couldn't save the cost");
    setSaved(true);
    toast.ok("Cost added: attach its invoice and receipt below");
  };
  const remove = async () => {
    if (!confirm("Delete this cost and its files?")) return;
    if (await mutate("production_cost.remove", { id: costId })) onClose();
  };
  return (
    <DialogLayer onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="w-[640px] rounded-xl border border-line bg-surface p-4 shadow-pop">
        <div className="text-[15px] font-semibold">{existing ? "Cost" : "Add a cost"}</div>
        <div className="mt-0.5 text-[12px] text-muted">
          For what the run's POs don't already have: freight and broker invoices, packing or labour fees, certification, samples, payment fees.
        </div>
        <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-2">
          <label className="flex flex-col gap-1 text-[12px]">
            <span className="text-muted">Category</span>
            <Select
              value={d.category}
              // Pre-sales --- a shipment's own cost stays shipping to backers (change the shipment instead).
              disabled={!!existing?.presale_shipment_id}
              options={COST_CATEGORIES.map((c) => ({
                value: c,
                label: `${COST_CATEGORY_LABELS[c]} → ${c === "backer_shipping" ? "the pre-sale, not per unit" : LAYER_LABELS[CATEGORY_LAYER[c]]}`,
              }))}
              onChange={(e) => {
                const category = e.target.value as CostCategory;
                set({ category, ...(category === "backer_shipping" && d.basis === "per_unit" ? { basis: "one_time" as Draft["basis"] } : {}) });
              }}
            />
          </label>
          <label className="flex flex-col gap-1 text-[12px]">
            <span className="text-muted">Vendor</span>
            <Select
              value={d.vendor_id ?? ""}
              options={[
                { value: "", label: "None" },
                ...vendors.filter((v) => !v.archived || v.id === d.vendor_id).map((v) => ({ value: v.id, label: v.short_name || v.name })),
              ]}
              onChange={(e) => set({ vendor_id: e.target.value || null })}
            />
          </label>
          <label className="col-span-2 flex flex-col gap-1 text-[12px]">
            <span className="text-muted">What it is</span>
            <Input value={d.description} placeholder="e.g. Sea freight Shenzhen → LA, 12 cartons" onChange={(e) => set({ description: e.target.value })} />
          </label>
          <label className="flex flex-col gap-1 text-[12px]">
            <span className="text-muted">Amount</span>
            <div className="flex gap-1.5">
              <Input value={d.amountText} inputMode="decimal" placeholder="0.00" onChange={(e) => set({ amountText: e.target.value })} className="font-mono" />
              <UpperInput value={d.currency} maxLength={3} onText={(currency) => set({ currency })} className="w-16 font-mono" aria-label="Currency" />
            </div>
          </label>
          <label className="flex flex-col gap-1 text-[12px]">
            <span className="text-muted">{usd ? "USD rate" : `USD per 1 ${d.currency}`}</span>
            <Input
              value={usd ? "1" : d.rateText}
              disabled={usd}
              inputMode="decimal"
              placeholder="e.g. 0.1389"
              onChange={(e) => set({ rateText: e.target.value })}
              className="font-mono"
            />
            {!usd && amount > 0 && rate > 0 && <span className="text-[11px] text-muted">= {money(Math.round(amount * rate * 100) / 100)}</span>}
          </label>
          <label className="flex flex-col gap-1 text-[12px]">
            <span className="text-muted">Basis</span>
            <Select
              value={d.basis}
              // Pre-sales --- shipping to backers is what the shipment cost: never per unit.
              options={Object.entries(COST_BASIS_LABELS)
                .filter(([value]) => d.category !== "backer_shipping" || value !== "per_unit")
                .map(([value, label]) => ({ value, label }))}
              onChange={(e) => set({ basis: e.target.value as Draft["basis"] })}
            />
          </label>
          <label className="flex flex-col gap-1 text-[12px]">
            <span className="text-muted">On PO (optional)</span>
            <Select
              value={d.po_id ?? ""}
              options={[
                { value: "", label: "None" },
                ...pos.map((p) => ({ value: p.id, label: `${p.number} · ${cx_.vendorById.get(p.vendor_id)?.short_name ?? ""}` })),
              ]}
              onChange={(e) => set({ po_id: e.target.value || null })}
            />
          </label>
          <label className="flex flex-col gap-1 text-[12px]">
            <span className="text-muted">Invoice number</span>
            <Input value={d.invoice_number} onChange={(e) => set({ invoice_number: e.target.value })} className="font-mono" />
          </label>
          <label className="flex flex-col gap-1 text-[12px]">
            <span className="text-muted">Invoice date (the date on it)</span>
            <Input type="date" value={d.invoice_date ?? ""} onChange={(e) => set({ invoice_date: e.target.value || null })} />
          </label>
          <div className="col-span-2 rounded-md border border-line p-2">
            <div className="mb-1.5 flex items-center gap-2 text-[12px]">
              <span className="font-medium">How it was paid</span>
              <CostWise
                costId={costId}
                saved={saved}
                vendorId={d.vendor_id ?? (d.po_id ? (pos.find((x) => x.id === d.po_id)?.vendor_id ?? null) : null)}
                oneTime={d.basis === "one_time"}
                amount={amount}
                currency={d.currency.toUpperCase()}
                date={d.invoice_date ?? d.paid_on}
                label={`${run.number} cost: ${d.description || COST_CATEGORY_LABELS[d.category as CostCategory]}`}
                paid={d.paid_on ? { on: d.paid_on, method: d.paid_method, ref: d.paid_ref } : null}
                onPaidBy={(patch) => set(patch)}
              />
            </div>
            <div className="mb-1 text-[11px] text-faint">Paid another way (card, wire, PayPal…), or before HHL Ops knew the transfer:</div>
            <div className="grid grid-cols-3 gap-2">
              <Input type="date" value={d.paid_on ?? ""} onChange={(e) => set({ paid_on: e.target.value || null })} aria-label="Paid on" />
              <Select
                value={d.paid_method}
                options={[{ value: "", label: "How paid…" }, ...PAID_METHODS.map((m) => ({ value: m, label: PAID_METHOD_LABELS[m] }))]}
                onChange={(e) => set({ paid_method: e.target.value as Draft["paid_method"] })}
              />
              <Input value={d.paid_ref} placeholder="Payment reference" onChange={(e) => set({ paid_ref: e.target.value })} />
            </div>
            {d.paid_method && (
              <PaidWith
                method={d.paid_method}
                accountId={d.paid_account_id}
                via={d.paid_via}
                onChange={(p) =>
                  set({ ...(p.accountId !== undefined ? { paid_account_id: p.accountId } : {}), ...(p.via !== undefined ? { paid_via: p.via } : {}) })
                }
                className="mt-2 grid grid-cols-2 gap-2"
              />
            )}
            <div className="mt-1 text-[11px] text-faint">Attach the payment receipt below.</div>
          </div>
          <label className="col-span-2 flex flex-col gap-1 text-[12px]">
            <span className="text-muted">Notes</span>
            <Textarea value={d.notes} onChange={(e) => set({ notes: e.target.value })} className="min-h-14" />
          </label>
        </div>
        {d.category === "unknown" && (
          <div className="mt-2 rounded-md bg-warn-soft/60 px-3 py-2 text-[12px]">
            Paid before its invoice came? Keep it as not known yet: it counts under Other, and the run lists it under Needs attention until its category is set
            and its invoice is attached.
          </div>
        )}
        {oneTime && (
          <div className="mt-2 rounded-md bg-accent-soft/60 px-3 py-2 text-[12px]">
            A {COST_CATEGORY_LABELS[d.category as CostCategory].toLowerCase()} here is all on this run's units. To spread it over future runs (a mold over 5,000
            units), add it to the tooling ledger on the project's Costs tab instead.
          </div>
        )}
        <div className="mt-3">
          <div className="mb-1 text-[12px] font-medium">Invoice, receipt and photos</div>
          <div className="mb-1.5 text-[11px] text-faint">
            No invoice, it came by email? Save the email as a PDF or a screenshot and drop it here as an Email: it counts as the cost's document.
          </div>
          {saved ? (
            <Attachments
              owner={{ type: "production_cost", id: costId }}
              kind="invoice"
              hint={d.category === "backer_shipping" ? PRESALE_FILE_HINT : undefined}
            />
          ) : (
            <div className="text-[12px] text-faint">Save the cost first, then drop its files here.</div>
          )}
        </div>
        <div className="mt-4 flex items-center gap-2">
          {saved && can("production_cost.remove") && (
            <Button variant="danger" onClick={remove}>
              Delete
            </Button>
          )}
          <div className="ml-auto flex gap-2">
            <Button variant="ghost" onClick={onClose}>
              {saved && !existing ? "Done" : "Cancel"}
            </Button>
            <Button variant="primary" onClick={() => save()}>
              {saved ? "Save" : "Add cost"}
            </Button>
          </div>
        </div>
      </div>
    </DialogLayer>
  );
}

/**
 * Find its Wise transfer (Mitch, 2026-11-03: "there's no clear way to go and search/pick a wise transaction we already
 * did"): a cost paid by a Wise transfer says which; otherwise its button opens WisePicker (bills/WisePicker.tsx), which
 * links it through Choose… (a bill is made for the cost, as Wise history's Choose… does). Only what Choose… can list: a
 * saved one-time cost with a vendor (its own, else its PO's), by someone who can pay.
 */
function CostWise({
  costId,
  saved,
  vendorId,
  oneTime,
  amount,
  currency,
  date,
  label,
  paid,
  onPaidBy,
}: {
  costId: string;
  saved: boolean;
  vendorId: string | null;
  oneTime: boolean;
  amount: number;
  currency: string;
  date: string | null;
  label: string;
  /** How the dialog says it was paid (Paid another way), if it says. */
  paid: { on: string; method: string; ref: string } | null;
  /** The dialog takes the transfer picked as how it was paid, so its Save keeps it. */
  onPaidBy: (patch: { paid_on: string | null; paid_method: "wise"; paid_ref: string; paid_account_id: null; paid_via: "" }) => void;
}) {
  const canPay = useCanPay();
  const billLines = useRows("bill_line");
  const allocs = useRows("payment_allocation");
  const payments = useRows("payment");
  const [picking, setPicking] = useState(false);
  const billIds = new Set(billLines.filter((l) => l.production_cost_id === costId).map((l) => l.bill_id));
  const paidBy = billIds.size ? payments.filter((p) => p.status === "paid" && allocs.some((a) => a.payment_id === p.id && billIds.has(a.bill_id))) : [];
  if (paidBy.length)
    return (
      <span className="ml-auto truncate text-[12px] text-ok">
        Paid:{" "}
        {paidBy
          .map((p) =>
            [
              p.number,
              p.wise_transfer_id ? `Wise ${p.wise_transfer_id}` : PAID_METHOD_LABELS[p.method as keyof typeof PAID_METHOD_LABELS],
              p.paid_on ? shortDate(String(p.paid_on).slice(0, 10)) : "",
            ]
              .filter(Boolean)
              .join(" · "),
          )
          .join("; ")}
      </span>
    );
  const why = !saved
    ? "Add the cost first"
    : !vendorId
      ? "Say who it's from first: a transfer pays a vendor"
      : !oneTime
        ? "Only a one-time cost is paid by a transfer"
        : !(amount > 0)
          ? "Enter its amount first"
          : !canPay
            ? "Linking a payment needs Can pay"
            : "";
  return (
    <>
      <Button size="sm" className="ml-auto" icon={<Search className="size-3.5" />} disabled={!!why} title={why || undefined} onClick={() => setPicking(true)}>
        Find its Wise transfer…
      </Button>
      {picking && vendorId && (
        <WisePicker
          target={{ kind: "production_cost", id: costId, vendorId, amount, currency, date, label }}
          note={
            paid && paid.method !== "wise"
              ? `It says it was paid by ${PAID_METHOD_LABELS[paid.method as keyof typeof PAID_METHOD_LABELS] ?? "another way"} on ${shortDate(paid.on)}: the transfer you pick replaces that.`
              : undefined
          }
          beforePick={async (t) => {
            // The transfer picked is how it was paid: its paid fields name it (TRANSFER-<number>), which is what Choose…
            // ticks it by (paidElsewhere() in shared/bills.ts); a wire or a card recorded before would leave it out.
            const ref = `TRANSFER-${t.wise_transfer_id}`;
            if (paid?.method === "wise" && (paid.ref.match(/\d+/g) ?? ([] as string[])).includes(String(t.wise_transfer_id))) return true;
            const patch = {
              paid_on: t.transfer_date ? String(t.transfer_date).slice(0, 10) : (paid?.on ?? null),
              paid_method: "wise" as const,
              paid_ref: ref,
              paid_account_id: null,
              paid_via: "" as const,
            };
            if (!(await mutate("production_cost.update", { id: costId, patch }))) return false;
            onPaidBy(patch);
            return true;
          }}
          onClose={() => setPicking(false)}
        />
      )}
    </>
  );
}
